import { useEffect, useState } from "react";
import { ShieldCheck, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const MODULE_LABEL = {
    listings: "Inserate", inquiries: "Anfragen", wms: "Lager / WMS",
    workshop: "Werkstatt", accounting: "Buchhaltung", dispo: "Disposition",
};

/**
 * EmployeeAccessRequestsCard — offene Modul-Freigabe-Anfragen bestätigter
 * Mitarbeiter, angezeigt im „Mitarbeiter-Auslastung"-Tab. Der Händler kann
 * direkt freigeben/ablehnen (gleiche Endpoints wie in den Einstellungen).
 */
export default function EmployeeAccessRequestsCard({ onChange }) {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState("");

    useEffect(() => {
        let alive = true;
        api.get("/dealer/access-requests")
            .then((r) => { if (alive) setRows(r.data.items || []); })
            .catch(() => { if (alive) setRows([]); });
        return () => { alive = false; };
    }, []);

    const decide = async (req, action) => {
        setBusy(req.id);
        try {
            await api.post(`/dealer/access-requests/${req.id}/${action}`);
            toast.success(action === "grant" ? "Modul freigegeben" : "Anfrage abgelehnt");
            setRows((rs) => rs.filter((x) => x.id !== req.id));
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    if (rows === null || rows.length === 0) return null;

    return (
        <div className="bg-white border-2 border-amber-300 rounded-sm p-4 mb-4" data-testid="access-requests-card">
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-amber-700" />
                <h3 className="font-display font-black text-base">Freigabe-Anfragen ({rows.length})</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Mitarbeiter möchten Zugriff auf weitere Firmen-Bereiche. Direkt freigeben oder ablehnen.</p>
            <ul className="space-y-2">
                {rows.map((r) => (
                    <li key={r.id} className="flex items-center gap-2 flex-wrap border border-amber-200 bg-amber-50 rounded-sm px-3 py-2" data-testid={`access-request-card-${r.id}`}>
                        <span className="text-sm font-bold text-gray-900 truncate">{r.employee_label}</span>
                        <span className="text-xs text-gray-500">möchte Zugriff auf</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-white border border-amber-200 text-gray-700 px-1.5 py-0.5 rounded-sm">
                            {MODULE_LABEL[r.module] || r.module}
                        </span>
                        <div className="ml-auto flex items-center gap-2">
                            <button
                                onClick={() => decide(r, "grant")}
                                disabled={busy === r.id}
                                className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-2.5 py-1.5 rounded-sm disabled:opacity-50"
                                data-testid={`access-card-grant-${r.id}`}
                            >
                                {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Freigeben
                            </button>
                            <button
                                onClick={() => decide(r, "reject")}
                                disabled={busy === r.id}
                                className="inline-flex items-center gap-1 border border-gray-300 hover:bg-white text-gray-700 text-xs font-bold px-2.5 py-1.5 rounded-sm disabled:opacity-50"
                                data-testid={`access-card-reject-${r.id}`}
                            >
                                <X className="w-3.5 h-3.5" /> Ablehnen
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
